import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { browserZone, inZone } from '../components/candidates/AlexDialogs';
import AlexReport from '../components/candidates/AlexReport';
import { Stepper } from '../components/candidates/ApplicationCard';
import { DecisionBox, RoundCard, Scorecard, ScorecardForm } from '../components/candidates/InterviewViews';
import { Evidence } from '../components/candidates/common';
import { GradeTile } from '../components/candidates/MatchPanel';
import { screenLine } from '../components/candidates/ScreenDialog';
import { api, btnSecondary, card, EmptyState, InlineError, Skeleton, ToastProvider } from '../components/ui/Kit';

// Bundle 5a/5b pages: My interviews (/my-interview-rounds), the interviewer's packet + scorecard (/interview-rounds/:id)
// and the HM candidate page (/applications/:id/review?tab= — the journey as tabs; works for an HM without Recruit).

function useAuth() {
  const navigate = useNavigate();
  useEffect(() => { if (!localStorage.getItem('token')) navigate('/'); }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

function Resume({ resume }) {
  if (!resume) return <p className="text-sm text-[#64748B]">No resume on file.</p>;
  if (resume.inline_url && /\.pdf$/i.test(resume.filename || '')) {
    return <div className="h-[70vh] min-h-[420px] rounded-lg border border-[#E6E9F0] overflow-hidden"><iframe title="Resume" src={resume.inline_url} className="w-full h-full" /></div>;
  }
  return resume.text ? <pre className="whitespace-pre-wrap text-[13px] text-[#334155] max-h-[70vh] overflow-y-auto" data-testid="resume-text">{resume.text}</pre>
    : <p className="text-sm text-[#64748B]">{resume.filename} — open it from the candidate’s profile.</p>;
}

function Section({ title, children, testid }) {
  return <section className={card + ' p-5'} data-testid={testid}><h2 className="text-base font-extrabold mb-2">{title}</h2>{children}</section>;
}

// ── My interviews (Part C): Upcoming (with the candidate's answer), Feedback due (countdown), Submitted, Coming up ──
export function countdown(iso, now = Date.now()) {
  const ms = new Date(iso).getTime() - now;
  const h = Math.round(Math.abs(ms) / 3600000);
  const t = h >= 48 ? `${Math.round(h / 24)} days` : h >= 1 ? `${h} h` : 'under an hour';
  return ms >= 0 ? `due in ${t}` : `overdue by ${t}`;
}

function MyBody() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api('/api/my-interviews').then(setD).catch((e) => setError(e.message)); }, []);
  if (error) return <div className={card + ' p-6'}><InlineError>{error}</InlineError></div>;
  if (!d) return <div className={card + ' p-6 space-y-2'} aria-busy="true"><Skeleton /><Skeleton /></div>;
  const submitted = d.submitted || d.done || [];
  const list = (rows, testid, empty, extra) => (!rows.length ? <p className="text-sm text-[#64748B]">{empty}</p> : (
    <ul className="divide-y divide-[#EEF1F6]" data-testid={testid}>
      {rows.map((x) => (
        <li key={x.round_id} className="py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 text-sm">
          <span className="flex-1 min-w-0 break-words"><b>{x.candidate}</b> · {x.job_title} · Round {x.round_number} ({x.name}){x.is_lead ? ' · you lead' : ''}
            <span className="block text-xs text-[#64748B]">{inZone(x.scheduled_start, browserZone())} · {x.mode_label}{x.location ? ` · ${x.location}` : ''}</span>
            {extra && extra(x)}</span>
          <button className={btnSecondary + ' text-xs py-1.5 self-start sm:self-auto'} onClick={() => navigate(x.link)}>{x.submitted ? 'Open' : 'Open overview'}</button>
        </li>))}
    </ul>));
  return (
    <div className="space-y-4" data-testid="my-interviews">
      <section className={card + ' p-4 sm:p-5'}><h1 className="text-xl sm:text-2xl font-extrabold">My interviews</h1>
        <p className="text-sm text-[#64748B]">Rounds you’re on: the candidate’s overview, your notes, then your feedback.</p></section>
      <Section title={`Feedback due (${d.feedback_due.length})`}>{list(d.feedback_due, 'feedback-due', 'Nothing due.', (x) => (
        <span className={`block text-xs font-semibold ${x.overdue ? 'text-[#B91C1C]' : 'text-[#B45309]'}`} data-testid="due-countdown">Feedback {countdown(x.due_at)}</span>))}</Section>
      <Section title={`Upcoming (${d.upcoming.length})`}>{list(d.upcoming, 'upcoming', 'No upcoming interviews.', (x) => (
        x.response ? <span className="block text-xs text-[#334155]" data-testid="upcoming-response">Candidate: {x.response}</span> : null))}</Section>
      <Section title={`Submitted (${submitted.length})`}>{list(submitted, 'submitted', 'Nothing submitted yet.')}</Section>
      <Section title="Coming up" testid="coming-up">
        {!(d.coming_up || []).length ? <p className="text-sm text-[#64748B]">No planned rounds with you yet.</p> : (
          <ul className="divide-y divide-[#EEF1F6]">{d.coming_up.map((x) => (
            <li key={`${x.req_id}.${x.round_number}`} className="py-2 text-sm flex flex-wrap justify-between gap-2" data-testid="coming-up-row">
              <span className="min-w-0 break-words">{x.job_title} · Round {x.round_number} ({x.name})</span>
              <b className="text-[#0F172A]">{x.candidates} candidate{x.candidates === 1 ? '' : 's'}</b></li>))}</ul>)}
        <p className="text-xs text-[#64748B] mt-2">Counts only — names appear once a round is scheduled with you.</p>
      </Section>
    </div>
  );
}

export function MyInterviewsPage() {
  useAuth();
  return <ToastProvider><AppShell active="myinterviews"><MyBody /></AppShell></ToastProvider>;
}

// ── The interviewer's packet ──
function PacketBody() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(() => api(`/api/interview-rounds/${id}`).then(setD).catch((e) => setError(e.message)), [id]);
  useEffect(() => { load(); }, [load]);
  if (error) return <div className={card + ' p-6'}><InlineError>{error}</InlineError></div>;
  if (!d) return <div className={card + ' p-6 space-y-2'} aria-busy="true"><Skeleton /><Skeleton /></div>;
  const r = d.round;
  const me = (JSON.parse(localStorage.getItem('user') || '{}')).id;
  const mine = r.scorecards.find((s) => s.interviewer_user_id === me) || null;
  return (
    <div className="space-y-4" data-testid="interview-packet">
      <section className={card + ' p-4 sm:p-5'}>
        <h1 className="text-xl sm:text-2xl font-extrabold break-words">{d.candidate.name}</h1>
        <p className="text-sm text-[#64748B]">{[d.candidate.title, d.candidate.company, d.candidate.location, d.candidate.years != null ? `${d.candidate.years} yrs` : null].filter(Boolean).join(' · ')}</p>
        <p className="text-sm mt-1">{d.job.req_id ? `${d.job.req_id} · ` : ''}{d.job.title}</p>
        {(d.job.summary || []).length > 0 && <div className="text-sm text-[#334155] mt-2 space-y-1" data-testid="job-summary">{d.job.summary.map((p) => <p key={p}>{p}</p>)}</div>}
      </section>
      <div className="grid lg:grid-cols-2 gap-4 items-start">
        <div className="space-y-4 min-w-0">
          {d.profile && <Section title="Profile" testid="overview-profile">
            {d.profile.summary && <p className="text-sm text-[#334155] whitespace-pre-line">{d.profile.summary}</p>}
            {d.profile.experience.length > 0 && <><h3 className="text-sm font-bold mt-3">Experience</h3>
              <ul className="text-sm mt-1 space-y-0.5">{d.profile.experience.map((e, i) => <li key={i} className="break-words">{[e.title, e.company].filter(Boolean).join(' · ')}{e.when ? <span className="text-[#64748B]"> · {e.when}</span> : null}</li>)}</ul></>}
            {d.profile.skills.length > 0 && <><h3 className="text-sm font-bold mt-3">Skills</h3>
              <div className="flex flex-wrap gap-1.5 mt-1">{d.profile.skills.map((x) => <span key={x} className="px-2 py-[3px] rounded-md text-[11.5px] font-semibold bg-[#F1F5F9] text-[#334155]">{x}</span>)}</div></>}
            {d.profile.certifications.length > 0 && <><h3 className="text-sm font-bold mt-3">Certifications</h3>
              <ul className="text-sm mt-1 list-disc pl-5">{d.profile.certifications.map((x) => <li key={x}>{x}</li>)}</ul></>}
          </Section>}
          <Section title="Resume"><Resume resume={d.resume} /></Section>
        </div>
        <div className="space-y-4 min-w-0">
          <section className={card + ' p-5'}>
            <ul className="space-y-3"><RoundCard round={r} canManage={false} /></ul>
            {(r.focus || []).length > 0 && <><h3 className="text-sm font-bold mt-3">Focus areas</h3>
              <div className="flex flex-wrap gap-1.5 mt-1" data-testid="focus-areas">{r.focus.map((f) => <span key={f.key} className="px-2 py-[3px] rounded-md text-[11.5px] font-semibold bg-[#EEF2FF] text-[#3730A3]">{f.label}</span>)}</div></>}
          </section>
          {r.me.on_panel && !r.me.submitted && !r.me.can_submit && !r.me.can_draft && r.status === 'scheduled' &&
            <p className="text-sm text-[#64748B]" data-testid="submit-opens">Your notes open {inZone(r.me.notes_open_at, browserZone())} (10 minutes before the start).</p>}
          {(r.me.can_submit || r.me.can_edit || r.me.can_draft) && <ScorecardForm round={r} existing={r.me.submitted ? mine : null} onSaved={(x) => setD(x)} />}
        </div>
      </div>
    </div>
  );
}

export function InterviewPacketPage() {
  useAuth();
  return <ToastProvider><AppShell active="myinterviews"><PacketBody /></AppShell></ToastProvider>;
}

// ── The HM candidate page ──
const RISK = { low: ['Low risk', 'bg-[#DCFCE7] text-[#166534]'], review: ['Review', 'bg-[#FEF3C7] text-[#92400E]'], high: ['High risk', 'bg-[#FEE2E2] text-[#991B1B]'] };
const MARK = { done: '✓', needs_you: '●', locked: '🔒', current: '' };

function OverviewTab({ d }) {
  const m = d.match;
  return (
    <div className="grid lg:grid-cols-2 gap-4 items-start" data-testid="tab-overview">
      <Section title="Resume"><Resume resume={d.resume} /></Section>
      <div className="space-y-4">
        <Section title="Match" testid="review-match">
          {!m ? <p className="text-sm text-[#64748B]">No match for this job.</p> : <>
            {m.why && <p className="text-sm"><Evidence text={m.why} quote={false} max={240} /></p>}
            <ul className="mt-2 text-[13px]">{(m.must_haves || []).filter((x) => x.on !== false).map((x) => (
              <li key={x.key}>{x.result === 'pass' ? '✓' : x.result === 'fail' ? '✗' : '?'} {x.label}{x.detail && x.detail !== x.label ? ` — ${x.detail}` : ''}</li>))}</ul></>}
        </Section>
        <Section title="Key facts"><dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[13px]" data-testid="review-facts">
          {d.key_facts.map((f) => <div key={f.key} data-testid={`review-fact-${f.key}`}><dt className="text-[#64748B]">{f.label}</dt><dd className="font-semibold">{f.text || '—'}</dd></div>)}</dl></Section>
        <Section title="Recruiter screen">{d.screen_summary && d.screen_summary.saved_at ? <p className="text-sm">{screenLine(d.screen_summary)}</p> : <p className="text-sm text-[#64748B]">Not screened.</p>}</Section>
      </div>
    </div>
  );
}

function AiTab({ d, onSaved }) {
  const ax = d.alex;
  if (!ax) return <EmptyState title="No AI screen" hint="The recruiters schedule the Quali5d AI screen (Alex)." />;
  return (
    <div className="space-y-4" data-testid="tab-ai">
      <p className="text-sm"><b>{ax.status}</b>{ax.score != null ? ` · ${Math.round(ax.score)} / ${ax.score_max}` : ''}{ax.recommendation ? ` · ${ax.recommendation}` : ''}</p>
      {ax.interview_id && <AlexReport interviewId={ax.interview_id} />}
      <DecisionBox kind="alex" review={d} onSaved={onSaved} />
    </div>
  );
}

function YourFeedback({ round: r, onSaved }) {
  const [open, setOpen] = useState(false);
  const me = (JSON.parse(localStorage.getItem('user') || '{}')).id;
  const mine = r.scorecards.find((s) => s.interviewer_user_id === me) || null;
  return (
    <section className={card + ' p-4'} data-testid="your-feedback">
      <h3 className="text-sm font-bold mb-2">Your feedback</h3>
      {mine ? <ul><Scorecard sc={mine} /></ul>
        : (r.me.can_submit || r.me.can_draft) ? (open ? <ScorecardForm round={r} existing={null} onSaved={() => { setOpen(false); onSaved(); }} />
          : <button type="button" className={btnSecondary} onClick={() => setOpen(true)} data-testid="your-feedback-open">{r.me.can_submit ? 'Submit feedback' : 'Take notes'}</button>)
          : r.status === 'scheduled' ? <p className="text-sm text-[#64748B]" data-testid="your-feedback-opens">Your notes open {inZone(r.me.notes_open_at || r.me.submit_opens_at, browserZone())}.</p>
            : <p className="text-sm text-[#64748B]">Feedback is closed for this round.</p>}
    </section>
  );
}

function RoundTab({ d, n, onSaved, reload }) {
  const r = [...d.rounds].reverse().find((x) => x.round_number === n);
  if (!r) return <EmptyState title={`Round ${n} isn’t scheduled yet`} hint="The recruiters schedule it once the previous step is approved." />;
  return (
    <div className="space-y-4" data-testid={`tab-round-${n}`}>
      <ul><RoundCard round={r} canManage={false} /></ul>
      {(r.focus || []).length > 0 && <div className="flex flex-wrap gap-1.5" data-testid="focus-areas">{r.focus.map((f) => <span key={f.key} className="px-2 py-[3px] rounded-md text-[11.5px] font-semibold bg-[#EEF2FF] text-[#3730A3]">{f.label}</span>)}</div>}
      {r.me && r.me.on_panel && <YourFeedback round={r} onSaved={reload} />}
      <DecisionBox kind="round" review={d} round={r} onSaved={onSaved} />
    </div>
  );
}

function ReviewBody() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [want, setWant] = useState(() => new URLSearchParams(window.location.search).get('tab'));
  const load = useCallback(() => api(`/api/applications/${id}/review`).then(setD).catch((e) => setError(e.message)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return error ? <div className={card + ' p-6'}><InlineError>{error}</InlineError></div> : <div className={card + ' p-6 space-y-2'} aria-busy="true"><Skeleton /><Skeleton /></div>;
  const tab = d.tabs.some((x) => x.key === want && x.enabled) ? want : d.open_tab;
  const go = (k) => { setWant(k); window.history.replaceState(null, '', `${window.location.pathname}?tab=${k}`); };
  const m = d.match;
  const risk = RISK[(d.authenticity || {}).risk];
  return (
    <div className="space-y-4 pb-4" data-testid="review-package">
      <section className={card + ' p-4 sm:p-5'}>
        <div className="flex flex-wrap items-start gap-3">
          {m && m.status === 'done' && <GradeTile grade={m.grade} total={m.total} />}
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-extrabold break-words">{d.candidate.name}
              {risk && <span className={`ml-2 align-middle px-2 py-0.5 rounded-full text-[11px] font-semibold ${risk[1]}`} data-testid="authenticity-badge">{risk[0]}</span>}</h1>
            <p className="text-sm text-[#64748B]">{[d.candidate.title, d.candidate.company, d.candidate.location].filter(Boolean).join(' · ')}</p>
            <p className="text-sm mt-1">{d.job.req_id ? `${d.job.req_id} · ` : ''}{d.job.title} · {d.candidate.code}</p>
          </div>
        </div>
        <div className="overflow-x-auto"><Stepper steps={(d.application.progress && d.application.progress.steps) || []} /></div>
        {d.needs_you && <button type="button" onClick={() => go(d.needs_you.tab)} data-testid="needs-you-banner"
          className="mt-3 w-full text-left rounded-xl bg-[#FEF3C7] text-[#92400E] px-3 py-2 text-sm font-semibold">{d.needs_you.text} →</button>}
      </section>
      <div className="flex gap-1 p-1 bg-[#EEF1F6] rounded-xl overflow-x-auto max-w-full" role="tablist" aria-label="Candidate journey" data-testid="review-tabs">
        {d.tabs.map((x) => (
          <button key={x.key} type="button" role="tab" aria-selected={tab === x.key} disabled={!x.enabled} onClick={() => go(x.key)} data-testid={`rtab-${x.key}`} data-state={x.state}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold whitespace-nowrap shrink-0 disabled:opacity-50 ${tab === x.key ? 'bg-white text-[#0F172A] shadow' : 'text-[#475569]'}`}>
            {x.label}{MARK[x.state] ? <span className={`ml-1 ${x.state === 'needs_you' ? 'text-[#D97706]' : ''}`} aria-label={x.state.replace('_', ' ')}>{MARK[x.state]}</span> : null}</button>))}
      </div>
      {tab === 'overview' && <OverviewTab d={d} />}
      {tab === 'ai' && <AiTab d={d} onSaved={setD} />}
      {tab.startsWith('round-') && <RoundTab d={d} n={Number(tab.split('-')[1])} onSaved={setD} reload={load} />}
      {tab === 'offer' && <section className={card + ' p-5'} data-testid="tab-offer"><h2 className="text-base font-extrabold">Offer — coming in bundle 6</h2>
        <p className="text-sm text-[#64748B] mt-1">Stage: {d.application.stage}{d.application.stage_state ? ` · ${d.application.stage_state.replace(/_/g, ' ')}` : ''}</p></section>}
      <button type="button" className={btnSecondary + ' text-xs'} onClick={load}>Refresh</button>
    </div>
  );
}

export function ReviewPage() {
  useAuth();
  return <ToastProvider><AppShell active="jobs"><ReviewBody /></AppShell></ToastProvider>;
}
